import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isAxiosError } from "axios";
import axiosInstance from "@/lib/axios";
import {
  razorpayConnectEndpoint,
  razorpayDisconnectEndpoint,
  razorpaySettingsEndpoint,
  razorpayIntegrationEndpoint,
  razorpayPaymentLinkCancelEndpoint,
  razorpayPaymentLinksEndpoint,
  razorpayWhatsappSenderEndpoint,
  razorpayWebhookSetupEndpoint,
  razorpayPaymentLinkSyncEndpoint,
  razorpayPaymentLinkSendEndpoint,
  razorpayPaidMoveEndpoint,
} from "@/config/endpoints";
import type { LeadTemplate } from "@/features/whatsapp/api/useLeadTemplates";
import { invalidateRecentActivityFeed } from "@/lib/queryClient";
import { refetchRoutes } from "@/hooks/api/useRouterData";

export interface RazorpayStatus {
  connected: boolean;
  isActive: boolean;
  isLive: boolean;
  keyIdMasked: string | null;
  accountName: string | null;
  connectedAt: string | null;
  /** Razorpay itself emails / SMSes every link Xale raises (RazorpayDeliveryToggle). Older servers omit it. */
  notifyCustomer?: boolean;
  /**
   * Where a deal goes when its link is paid ("paid → registered"). No target
   * for a pipeline = automatic (its last stage). Older servers omit it.
   */
  paidMove?: RazorpayPaidMove;
  /** How paid links reach Xale — see RazorpayWebhookPanel. */
  webhook?: { registered: boolean; url: string; lastEventAt: string | null };
}

export interface RazorpayPaidMoveTarget {
  stageId: number;
  statusId: number | null;
  subStatusId: number | null;
}

export interface RazorpayPaidMove {
  off: boolean;
  targets: RazorpayPaidMoveTarget[];
}

/** GET /integrations/razorpay/paid-move?childId= — the dialog's "When paid" line. */
export interface PaidMovePreview {
  moves: boolean;
  stageName: string | null;
  statusName: string | null;
  automatic?: boolean;
  reason?: string;
}

export interface RazorpayPaymentLink {
  id: number;
  rzpLinkId: string;
  rzpShortUrl: string;
  amount: number; // paise
  currency: string;
  status: "created" | "paid" | "expired" | "cancelled";
  expiresAt: string | null;
  paidAt: string | null;
  rzpPaymentId: string | null;
  dealPaymentId: number | null;
  sentVia: string | null;
  createdAt: string;
  /** The instalment the link bills (#seq), when the deal has a plan. */
  installmentId?: number | null;
  installmentSeq?: number | null;
}

/**
 * How the link goes out on the lead's WhatsApp number: a chat message (only
 * inside the 24-hour window) or an approved template whose `linkParam`
 * variable gets the link.
 */
export type PaymentLinkWhatsapp =
  | { mode: "text" }
  | {
      mode: "template";
      templateId: number | string;
      templateName: string;
      variables: Record<string, string>;
      linkParam: string;
    };

export interface CreatePaymentLinkPayload {
  childId: number;
  amount: number; // rupees
  expiresInDays?: number;
  sentVia?: ("copy" | "whatsapp" | "email")[];
  whatsapp?: PaymentLinkWhatsapp;
  /** Bill one instalment: paying the link settles it. */
  installmentId?: number;
  note?: string;
}

/** What "Send via → WhatsApp" can do for one lead. */
export interface PaymentWhatsappSender {
  available: boolean;
  reason?: string;
  provider?: LeadTemplate["provider"];
  /** "Wati · NiFan Wati", "WhatsApp Business · +91…". */
  label?: string;
  hasChat: boolean;
  /** The customer wrote in the last 24 hours — a plain message can go. */
  windowOpen: boolean;
  templates: LeadTemplate[];
  templatesError?: string;
}

export interface CreatePaymentLinkResult {
  paymentLink: {
    id: number;
    rzpLinkId: string;
    rzpShortUrl: string;
    amount: number;
    status: string;
    expiresAt: string | null;
    createdAt: string;
  };
  delivery: {
    copy: boolean;
    whatsapp?: { ok: boolean; error?: string };
    email?: { ok: boolean; error?: string };
  };
}

function useInvalidateRazorpayAndPaymentQueries() {
  const queryClient = useQueryClient();
  return (childId?: number) => {
    // Deal-level payment-link activity. Dropped `refetchType: "all"` so we
    // refetch only mounted observers (the open deal's link list / payment
    // widgets), not every background query.
    queryClient.invalidateQueries({ queryKey: ["razorpay-links", childId] });
    queryClient.invalidateQueries({ queryKey: ["razorpay-links"] });
    queryClient.invalidateQueries({ queryKey: ["childs"] });
    queryClient.invalidateQueries({ queryKey: ["get-child"] });
    queryClient.invalidateQueries({ queryKey: ["payments"] });
    queryClient.invalidateQueries({ queryKey: ["payment-summary"] });
    queryClient.invalidateQueries({ queryKey: ["activity-log"] });
    invalidateRecentActivityFeed();
  };
}

export function useRazorpayStatus() {
  return useQuery<{ status: string; data: RazorpayStatus }>({
    queryKey: ["razorpay-status"],
    queryFn: async () => {
      const res = await axiosInstance.get(razorpayIntegrationEndpoint);
      return res.data;
    },
  });
}

/** True while the workspace has an active Razorpay connection. */
export function useRazorpayConnected(): boolean {
  const { data } = useRazorpayStatus();
  return !!data?.data?.connected && !!data?.data?.isActive;
}

export function useConnectRazorpay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { keyId: string; keySecret: string; isLive?: boolean }) => {
      const res = await axiosInstance.post(razorpayConnectEndpoint, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Razorpay connected");
      queryClient.invalidateQueries({ queryKey: ["razorpay-status"] });
      // The sidebar's INTEGRATION group lists Razorpay only while connected
      // (server /tabs) — refresh it now, not in five minutes.
      refetchRoutes();
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || err.message || "Failed to connect Razorpay";
      toast.error(msg);
    },
  });
}

/** "Let Razorpay email and SMS every payment link" — admins / manage_integration (the server refuses others). */
export function useUpdateRazorpaySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { notifyCustomer: boolean } | { paidMove: RazorpayPaidMove | null }) => {
      const res = await axiosInstance.patch(razorpaySettingsEndpoint, payload);
      return res.data as { status: string; data: RazorpayStatus };
    },
    onSuccess: (data, variables) => {
      if ("paidMove" in variables) toast.success("Saved — paid deals will follow this");
      else
        toast.success(data.data.notifyCustomer ? "Razorpay will email and SMS every payment link" : "Razorpay will not send links itself");
      queryClient.invalidateQueries({ queryKey: ["razorpay-status"] });
      queryClient.invalidateQueries({ queryKey: ["razorpay-paid-move"] });
      queryClient.invalidateQueries({ queryKey: ["razorpay-console"] });
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || err.message || "Could not change the Razorpay setting";
      toast.error(msg);
    },
  });
}

export function useDisconnectRazorpay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(razorpayDisconnectEndpoint);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Razorpay disconnected");
      queryClient.invalidateQueries({ queryKey: ["razorpay-status"] });
      refetchRoutes();
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || err.message || "Failed to disconnect Razorpay";
      toast.error(msg);
    },
  });
}

export function useCreatePaymentLink() {
  const invalidate = useInvalidateRazorpayAndPaymentQueries();
  return useMutation({
    mutationFn: async (payload: CreatePaymentLinkPayload) => {
      const res = await axiosInstance.post(razorpayPaymentLinksEndpoint, payload);
      return res.data as { status: string; data: CreatePaymentLinkResult };
    },
    onSuccess: (data, variables) => {
      const d = data.data.delivery;
      const failed: string[] = [];
      if (d.whatsapp?.ok === false) failed.push(`WhatsApp: ${d.whatsapp.error || "failed"}`);
      if (d.email?.ok === false) failed.push(`Email: ${d.email.error || "failed"}`);
      if (failed.length) {
        // The link exists either way — say so, and why the send didn't go.
        toast.warning(`Payment link created, but not sent. ${failed.join(" · ")}`, {
          duration: 10000,
        });
      } else {
        const parts: string[] = ["Payment link created"];
        if (d.whatsapp?.ok) parts.push("WhatsApp sent");
        if (d.email?.ok) parts.push("Email sent");
        toast.success(parts.join(" · "));
      }
      invalidate(variables.childId);
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || err.message || "Failed to create payment link";
      toast.error(msg);
    },
  });
}

export function usePaymentWhatsappSender(leadId: number | null | undefined, enabled = true) {
  return useQuery<PaymentWhatsappSender>({
    queryKey: ["razorpay-whatsapp-sender", leadId],
    enabled: !!leadId && enabled,
    // Templates are proxied live from the provider; don't refetch per open.
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const res = await axiosInstance.get(razorpayWhatsappSenderEndpoint, {
        params: { leadId },
      });
      return res.data.data;
    },
  });
}

export function useListPaymentLinks(childId: number | null) {
  return useQuery<{ status: string; data: RazorpayPaymentLink[] }>({
    queryKey: ["razorpay-links", childId],
    enabled: !!childId,
    queryFn: async () => {
      const res = await axiosInstance.get(razorpayPaymentLinksEndpoint, {
        params: { childId },
      });
      return res.data;
    },
  });
}

/** Webhook URL + secret to paste in Razorpay — admins only (server 403s others). */
export function useRazorpayWebhookSetup(enabled: boolean) {
  return useQuery<{ url: string; secret: string; events: string[] }>({
    queryKey: ["razorpay-webhook-setup"],
    enabled,
    retry: false,
    queryFn: async () => {
      const res = await axiosInstance.get(razorpayWebhookSetupEndpoint);
      return res.data.data;
    },
  });
}

/** "Check now": ask Razorpay where a link stands; a paid one is recorded. */
export function useSyncPaymentLink() {
  const invalidate = useInvalidateRazorpayAndPaymentQueries();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axiosInstance.post(razorpayPaymentLinkSyncEndpoint(id));
      return res.data.data as { razorpayStatus: string };
    },
    onSuccess: (data) => {
      const s = data?.razorpayStatus;
      if (s === "paid") toast.success("Paid — the payment is recorded on the deal");
      else if (s === "expired" || s === "cancelled") toast.info(`The link has ${s}`);
      else toast.info("Not paid yet");
      invalidate();
    },
    onError: (err) => {
      const server = isAxiosError(err) ? err.response?.data?.message : undefined;
      toast.error(server || err.message || "Could not check the link");
    },
  });
}

export function useCancelPaymentLink() {
  const invalidate = useInvalidateRazorpayAndPaymentQueries();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axiosInstance.post(razorpayPaymentLinkCancelEndpoint(id));
      return res.data;
    },
    onSuccess: () => {
      toast.success("Payment link cancelled");
      invalidate();
    },
    onError: (err: any) => {
      const msg = err.response?.data?.message || err.message || "Failed to cancel link";
      toast.error(msg);
    },
  });
}

/** Where this deal goes the moment a link is paid ("When paid" line). */
export function usePaidMovePreview(childId: number | null | undefined, enabled = true) {
  return useQuery<PaidMovePreview>({
    queryKey: ["razorpay-paid-move", childId],
    enabled: !!childId && enabled,
    retry: false,
    queryFn: async () => {
      const res = await axiosInstance.get(razorpayPaidMoveEndpoint, { params: { childId } });
      return res.data.data;
    },
  });
}

/** Sends an already-created open link on WhatsApp (the lead's number) or email. */
export function useSendExistingPaymentLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: number; via: "whatsapp" | "email"; whatsapp?: PaymentLinkWhatsapp }) => {
      const res = await axiosInstance.post(razorpayPaymentLinkSendEndpoint(input.id), {
        via: input.via,
        whatsapp: input.whatsapp,
      });
      return res.data;
    },
    onSuccess: (_data, input) => {
      toast.success(input.via === "whatsapp" ? "Sent on WhatsApp" : "Sent by email");
      queryClient.invalidateQueries({ queryKey: ["razorpay-links"] });
      queryClient.invalidateQueries({ queryKey: ["activity-log"] });
    },
    onError: (err, input) => {
      const msg = (isAxiosError(err) ? err.response?.data?.message : undefined) || err.message;
      toast.error(`${input.via === "whatsapp" ? "WhatsApp" : "Email"} not sent: ${msg || "failed"}`, { duration: 9000 });
    },
  });
}
